<script setup lang="ts">
import { computed } from 'vue'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Search, Filter, X, ChevronDown } from 'lucide-vue-next'
import type { IDataTableFilter, IDataTableUtilityParams } from './types'

const props = defineProps<{
    searchable?: boolean
    filters?: IDataTableFilter[]
    modelValue: IDataTableUtilityParams
}>()

const emit = defineEmits<{
    'update:modelValue': [value: IDataTableUtilityParams]
}>()

const updateSearch = (value: string | number) => {
    emit('update:modelValue', {
        ...props.modelValue,
        search: value.toString(),
    })
}

const updateFilter = (column: string, value: string) => {
    emit('update:modelValue', {
        ...props.modelValue,
        filter: { column, value },
    })
}

const clearFilter = () => {
    emit('update:modelValue', {
        ...props.modelValue,
        filter: { column: '', value: '' },
    })
}

const activeFilter = computed(() => {
    if (!props.modelValue.filter || !props.modelValue.filter.column) {
        return null
    }

    const filter = props.filters?.find(f => f.column === props.modelValue.filter?.column)
    if (!filter) return null

    const value = filter.values.find(v => v.value === props.modelValue.filter?.value)
    if (!value) return null

    return {
        label: filter.label,
        value: value.label,
    }
})
</script>

<template>
    <div class="flex w-full items-center gap-3 lg:w-auto">
        <div v-if="searchable" class="relative w-full lg:w-auto">
            <Search class="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" />
            <Input
                :model-value="modelValue.search"
                placeholder="Search"
                class="pl-9 w-full lg:w-[240px] h-10 text-sm bg-background border-border focus:border-ring"
                @update:model-value="updateSearch"
            />
        </div>

        <div v-if="filters && filters.length > 0" class="flex items-center gap-2">
            <DropdownMenu :modal="false">
                <DropdownMenuTrigger as-child>
                    <Button variant="outline" size="lg" class="min-w-[140px] justify-between hover:bg-muted transition-colors">
                        <span class="flex items-center">
                            <Filter class="h-4 w-4 mr-1.5" />
                            Filter
                        </span>
                        <ChevronDown class="h-4 w-4 ml-2 shrink-0 text-muted-foreground" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <template v-for="filter in filters">
                        <DropdownMenuItem
                            v-for="value in filter.values"
                            :key="`${filter.column}-${value.value}`"
                            @click="updateFilter(filter.column, value.value)"
                        >
                            {{ filter.label }}: {{ value.label }}
                        </DropdownMenuItem>
                    </template>
                </DropdownMenuContent>
            </DropdownMenu>

            <Badge v-if="activeFilter" variant="secondary" class="gap-1">
                {{ activeFilter.label }}: {{ activeFilter.value }}
                <button
                    type="button"
                    class="ml-1 hover:bg-accent rounded-full"
                    @click="clearFilter"
                >
                    <X class="h-3 w-3" />
                </button>
            </Badge>
        </div>

        <slot name="toolbar" />
    </div>
</template>
